import { LuCheck } from '@react-icons/all-files/lu/LuCheck'
import { LuSlidersHorizontal } from '@react-icons/all-files/lu/LuSlidersHorizontal'
import React from 'react'

import {
  AccessibleIcon,
  DropdownMenu,
  Flex,
  IconButton,
  Text,
  Theme,
} from 'components/ds'

type SearchOption = {
  value: string
  text: string
  dir?: string
}

type Category = {
  label: string
  options: SearchOption[]
}

const CATEGORY_LABELS: Record<string, string> = {
  score: 'Relevance',
  date: 'Date added',
  year: 'Year',
  'Release Date': 'Release date',
  name: 'Name',
  artist: 'Artist',
}

const DIRECTION_LABELS: Record<string, Record<string, string>> = {
  date: {
    SORT_DIRECTION_DESC: 'Newest first',
    SORT_DIRECTION_ASC: 'Oldest first',
  },
  year: {
    SORT_DIRECTION_DESC: 'Newest first',
    SORT_DIRECTION_ASC: 'Oldest first',
  },
  'Release Date': {
    SORT_DIRECTION_DESC: 'Newest first',
    SORT_DIRECTION_ASC: 'Oldest first',
  },
  name: { SORT_DIRECTION_ASC: 'A–Z', SORT_DIRECTION_DESC: 'Z–A' },
  artist: { SORT_DIRECTION_ASC: 'A–Z', SORT_DIRECTION_DESC: 'Z–A' },
}

const CATEGORY_ORDER = [
  'score',
  'date',
  'year',
  'Release Date',
  'name',
  'artist',
]

const groupByCategory = (options: SearchOption[]): Category[] => {
  const seen = new Map<string, Category>()
  for (const o of options) {
    if (!seen.has(o.value)) {
      seen.set(o.value, {
        label: CATEGORY_LABELS[o.value] ?? o.text,
        options: [],
      })
    }
    seen.get(o.value)!.options.push(o)
  }
  return CATEGORY_ORDER.flatMap(key => {
    const cat = seen.get(key)
    return cat ? [cat] : []
  })
}

const getDirectionLabel = (option: SearchOption): string => {
  if (!option.dir) return option.text
  return DIRECTION_LABELS[option.value]?.[option.dir] ?? option.text
}

type Props = {
  options: SearchOption[]
  searchSort: string
  searchSortDirection: string
  onSelect: (sort: string, sortDirection: string) => void
}

export function SearchSortMenu({
  options,
  searchSort,
  searchSortDirection,
  onSelect,
}: Props) {
  const categories = groupByCategory(options)

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger>
        <IconButton variant="ghost" size="2" color="gray" highContrast>
          <AccessibleIcon label="Sort search results">
            <LuSlidersHorizontal size={18} />
          </AccessibleIcon>
        </IconButton>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content variant="solid" align="end" size="2">
        <Theme appearance="light">
          {categories.map((category, i) => {
            const isCategorySelected = category.options.some(
              o => o.value === searchSort,
            )

            if (category.options.length === 1) {
              const o = category.options[0]
              if (!o) return null
              const selected =
                o.value === searchSort && o.dir === searchSortDirection
              return (
                <React.Fragment key={o.value}>
                  <DropdownMenu.Item
                    onSelect={() => onSelect(o.value, o.dir ?? '')}
                  >
                    <Flex align="center" justify="between" gap="4" flexGrow="1">
                      <Text size="2" weight={selected ? 'medium' : 'regular'}>
                        {category.label}
                      </Text>
                      {selected && (
                        <LuCheck size={14} color="var(--accent-9)" />
                      )}
                    </Flex>
                  </DropdownMenu.Item>
                  {i === 0 && <DropdownMenu.Separator />}
                </React.Fragment>
              )
            }

            return (
              <DropdownMenu.Sub key={category.label}>
                <DropdownMenu.SubTrigger>
                  <Text
                    size="2"
                    weight={isCategorySelected ? 'medium' : 'regular'}
                  >
                    {category.label}
                  </Text>
                </DropdownMenu.SubTrigger>
                <DropdownMenu.SubContent>
                  {category.options.map(o => {
                    const selected =
                      o.value === searchSort && o.dir === searchSortDirection
                    return (
                      <DropdownMenu.Item
                        key={`${o.value}-${o.dir}`}
                        onSelect={() => onSelect(o.value, o.dir ?? '')}
                      >
                        <Flex
                          align="center"
                          justify="between"
                          gap="4"
                          flexGrow="1"
                        >
                          <Text
                            size="2"
                            weight={selected ? 'medium' : 'regular'}
                          >
                            {getDirectionLabel(o)}
                          </Text>
                          {selected && (
                            <LuCheck size={14} color="var(--accent-9)" />
                          )}
                        </Flex>
                      </DropdownMenu.Item>
                    )
                  })}
                </DropdownMenu.SubContent>
              </DropdownMenu.Sub>
            )
          })}
        </Theme>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  )
}
